/**
 * Onboarding wizard -- 7 steps a new admin walks through after Stripe checkout.
 *
 * Behaviour:
 * - Loads `/api/onboarding/state` on mount; creates the row server-side if absent.
 * - Renders the current step's form; "Save and continue" PATCHes state and advances.
 * - Final step posts `/api/onboarding/complete`, which sets `Tenant.onboardingCompletedAt`
 *   and audits ONBOARDING_COMPLETED. We then navigate the admin to the dashboard.
 *
 * Backend stores step payloads opaquely under `data[stepId]` so adding fields to
 * any step never needs a Prisma migration.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronRight, ChevronLeft, Plus, Trash2, ExternalLink, Loader2, Upload, FileText, X } from 'lucide-react';
import { api, extractApiError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore } from '@/stores/auth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

const STEP_IDS = ['step1', 'step2', 'step3', 'step4', 'step5', 'step6', 'step7'] as const;
type StepId = (typeof STEP_IDS)[number];

const STEP_META: Record<StepId, { title: string; description: string }> = {
  step1: { title: 'Customer profile', description: 'Legal entity, country, regulatory contact.' },
  step2: { title: 'AI inventory', description: 'List the AI systems your team currently uses or evaluates.' },
  step3: { title: 'Risk register', description: 'Top risks across those systems for the AI risk register.' },
  step4: { title: 'GDPR ROPA seed', description: 'Article 30 processing-activity inventory starter.' },
  step5: { title: 'First document', description: 'Upload one regulatory document so we can show extraction live.' },
  step6: { title: 'First workflow', description: 'Pick one product workflow to enable on day one.' },
  step7: { title: 'Generate Compliance Pack', description: 'Run the first export. This finalises onboarding.' },
};

interface OnboardingState {
  stepIds: readonly StepId[];
  currentStep: StepId;
  completedSteps: StepId[];
  data: Record<string, unknown>;
  tenant: { id?: string; name?: string; onboardingCompletedAt?: string | null };
}

export function OnboardingPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const setSession = useAuthStore((s) => s.setSession);
  const accessToken = useAuthStore((s) => s.accessToken);
  const refreshToken = useAuthStore((s) => s.refreshToken);
  const sessionUser = useAuthStore((s) => s.user);
  const sessionTenant = useAuthStore((s) => s.tenant);

  const stateQ = useQuery({
    queryKey: ['onboarding', 'state'],
    queryFn: async () => (await api.get('/api/onboarding/state')).data as OnboardingState,
  });

  const [activeStep, setActiveStep] = useState<StepId>('step1');

  useEffect(() => {
    if (stateQ.data) setActiveStep(stateQ.data.currentStep);
  }, [stateQ.data?.currentStep]);

  const saveStep = useMutation({
    mutationFn: async ({ stepId, data, complete }: { stepId: StepId; data: Record<string, unknown>; complete: boolean }) =>
      (await api.patch('/api/onboarding/state', { stepId, data, complete })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['onboarding', 'state'] }),
    onError: (err) => toast.error('Save failed', extractApiError(err).message),
  });

  const complete = useMutation({
    mutationFn: async () => (await api.post('/api/onboarding/complete', {})).data as { completedAt: string },
    onSuccess: (data) => {
      // Refresh the stored tenant so any future redirect/check is up-to-date.
      if (sessionUser && sessionTenant && accessToken && refreshToken) {
        setSession({
          user: sessionUser,
          tenant: { ...sessionTenant, onboardingCompletedAt: data.completedAt },
          tokens: { accessToken, refreshToken, expiresIn: 0 },
        });
      }
      toast.success('Onboarding complete', 'Welcome to Praxara.');
      navigate('/', { replace: true });
    },
    onError: (err) => toast.error('Complete failed', extractApiError(err).message),
  });

  const data = stateQ.data?.data ?? {};
  const completedSet = useMemo(() => new Set(stateQ.data?.completedSteps ?? []), [stateQ.data?.completedSteps]);

  if (stateQ.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/30">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (stateQ.data?.tenant?.onboardingCompletedAt) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/30">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>Onboarding already complete</CardTitle>
            <CardDescription>This tenant finished onboarding on {new Date(stateQ.data.tenant.onboardingCompletedAt).toLocaleDateString()}.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/', { replace: true })}>Go to dashboard</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 p-6 lg:grid-cols-[260px_1fr]">
        {/* Step nav */}
        <aside className="lg:sticky lg:top-6 lg:self-start">
          <h1 className="font-display text-xl">Get started</h1>
          <p className="mt-1 text-xs text-muted-foreground">
            Seven steps to your first signed Compliance Pack.
          </p>
          <ol className="mt-4 space-y-1">
            {STEP_IDS.map((s, i) => {
              const isComplete = completedSet.has(s);
              const isActive = s === activeStep;
              return (
                <li key={s}>
                  <button
                    type="button"
                    onClick={() => setActiveStep(s)}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm transition-colors',
                      isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted',
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold',
                        isComplete
                          ? 'bg-emerald-500 text-white'
                          : isActive
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted-foreground/20 text-muted-foreground',
                      )}
                    >
                      {isComplete ? <Check className="h-3 w-3" /> : i + 1}
                    </span>
                    <span className="truncate">{STEP_META[s].title}</span>
                  </button>
                </li>
              );
            })}
          </ol>
        </aside>

        {/* Active step */}
        <main>
          <Card>
            <CardHeader>
              <CardTitle>{STEP_META[activeStep].title}</CardTitle>
              <CardDescription>{STEP_META[activeStep].description}</CardDescription>
            </CardHeader>
            <CardContent>
              {activeStep === 'step1' && (
                <Step1Profile
                  initial={(data.step1 as Record<string, unknown> | undefined) ?? {}}
                  onSave={(payload) => saveStep.mutate({ stepId: 'step1', data: payload, complete: true })}
                  saving={saveStep.isPending}
                />
              )}
              {activeStep === 'step2' && (
                <Step2Inventory
                  initial={(data.step2 as Record<string, unknown> | undefined) ?? {}}
                  onSave={(payload) => saveStep.mutate({ stepId: 'step2', data: payload, complete: true })}
                  saving={saveStep.isPending}
                />
              )}
              {activeStep === 'step3' && (
                <Step3Risks
                  initial={(data.step3 as Record<string, unknown> | undefined) ?? {}}
                  onSave={(payload) => saveStep.mutate({ stepId: 'step3', data: payload, complete: true })}
                  saving={saveStep.isPending}
                />
              )}
              {activeStep === 'step4' && (
                <Step4Ropa
                  initial={(data.step4 as Record<string, unknown> | undefined) ?? {}}
                  onSave={(payload) => saveStep.mutate({ stepId: 'step4', data: payload, complete: true })}
                  saving={saveStep.isPending}
                />
              )}
              {activeStep === 'step5' && (
                <Step5Doc
                  done={completedSet.has('step5')}
                  onComplete={(payload) => saveStep.mutate({ stepId: 'step5', data: payload, complete: true })}
                  saving={saveStep.isPending}
                />
              )}
              {activeStep === 'step6' && (
                <Step6Workflow
                  initial={(data.step6 as Record<string, unknown> | undefined) ?? {}}
                  onSave={(payload) => saveStep.mutate({ stepId: 'step6', data: payload, complete: true })}
                  saving={saveStep.isPending}
                />
              )}
              {activeStep === 'step7' && (
                <Step7Pack
                  onComplete={() => complete.mutate()}
                  completing={complete.isPending}
                  ready={STEP_IDS.slice(0, 6).every((s) => completedSet.has(s))}
                />
              )}
            </CardContent>
          </Card>

          <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
            <button
              type="button"
              onClick={() => {
                const idx = STEP_IDS.indexOf(activeStep);
                if (idx > 0) setActiveStep(STEP_IDS[idx - 1]);
              }}
              disabled={STEP_IDS.indexOf(activeStep) === 0}
              className="inline-flex items-center gap-1 disabled:opacity-50"
            >
              <ChevronLeft className="h-3 w-3" /> Previous
            </button>
            <span>
              Step {STEP_IDS.indexOf(activeStep) + 1} of {STEP_IDS.length}
            </span>
            <button
              type="button"
              onClick={() => {
                const idx = STEP_IDS.indexOf(activeStep);
                if (idx < STEP_IDS.length - 1) setActiveStep(STEP_IDS[idx + 1]);
              }}
              disabled={STEP_IDS.indexOf(activeStep) === STEP_IDS.length - 1}
              className="inline-flex items-center gap-1 disabled:opacity-50"
            >
              Next <ChevronRight className="h-3 w-3" />
            </button>
          </div>
        </main>
      </div>
    </div>
  );
}

// --- Step 1 -------------------------------------------------------------------

function Step1Profile({
  initial,
  onSave,
  saving,
}: {
  initial: Record<string, unknown>;
  onSave: (data: Record<string, unknown>) => void;
  saving: boolean;
}) {
  const [legalEntity, setLegalEntity] = useState((initial.legalEntity as string | undefined) ?? '');
  const [country, setCountry] = useState((initial.country as string | undefined) ?? 'GB');
  const [icoRegistration, setIco] = useState((initial.icoRegistration as string | undefined) ?? '');
  const [contactName, setContactName] = useState((initial.contactName as string | undefined) ?? '');
  const [contactEmail, setContactEmail] = useState((initial.contactEmail as string | undefined) ?? '');
  const valid = legalEntity.trim().length > 1 && country.trim().length === 2 && contactEmail.includes('@');
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid) return;
        onSave({ legalEntity, country, icoRegistration, contactName, contactEmail });
      }}
      className="space-y-4"
    >
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <Label htmlFor="legalEntity">Legal entity name</Label>
          <Input id="legalEntity" value={legalEntity} onChange={(e) => setLegalEntity(e.target.value)} required />
        </div>
        <div>
          <Label htmlFor="country">Country (ISO-3166)</Label>
          <Input id="country" value={country} onChange={(e) => setCountry(e.target.value.toUpperCase())} maxLength={2} required />
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="ico">UK ICO registration (Z-prefix)</Label>
          <Input id="ico" value={icoRegistration} onChange={(e) => setIco(e.target.value)} placeholder="ZA123456" />
          <p className="mt-1 text-xs text-muted-foreground">Optional outside the UK. Used in your DPA + ROPA artefacts.</p>
        </div>
        <div>
          <Label htmlFor="contactName">Primary regulatory contact</Label>
          <Input id="contactName" value={contactName} onChange={(e) => setContactName(e.target.value)} required />
        </div>
        <div>
          <Label htmlFor="contactEmail">Contact email</Label>
          <Input id="contactEmail" type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} required />
        </div>
      </div>
      <Button type="submit" disabled={!valid || saving}>
        {saving ? 'Saving...' : 'Save and continue'} <ChevronRight className="ml-1 h-3 w-3" />
      </Button>
    </form>
  );
}

// --- Step 2 -------------------------------------------------------------------

interface AiSystem {
  name: string;
  vendor: string;
  model: string;
  useCase: string;
  riskClass: 'minimal' | 'limited' | 'high' | 'unacceptable';
}

function Step2Inventory({
  initial,
  onSave,
  saving,
}: {
  initial: Record<string, unknown>;
  onSave: (data: Record<string, unknown>) => void;
  saving: boolean;
}) {
  const [systems, setSystems] = useState<AiSystem[]>(
    Array.isArray(initial.systems) && initial.systems.length > 0
      ? (initial.systems as AiSystem[])
      : [{ name: '', vendor: '', model: '', useCase: '', riskClass: 'limited' }],
  );

  const update = (i: number, patch: Partial<AiSystem>) =>
    setSystems((prev) => prev.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));

  const valid = systems.length > 0 && systems.every((s) => s.name.trim().length > 0);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid) return;
        onSave({ systems });
      }}
      className="space-y-4"
    >
      <div className="space-y-3">
        {systems.map((s, i) => (
          <div key={i} className="rounded-md border p-3">
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <Label>System name</Label>
                <Input value={s.name} onChange={(e) => update(i, { name: e.target.value })} required />
              </div>
              <div>
                <Label>Vendor</Label>
                <Input value={s.vendor} onChange={(e) => update(i, { vendor: e.target.value })} placeholder="e.g. OpenAI / Anthropic / internal" />
              </div>
              <div>
                <Label>Model</Label>
                <Input value={s.model} onChange={(e) => update(i, { model: e.target.value })} placeholder="e.g. gpt-4o, claude-3-5" />
              </div>
              <div>
                <Label>EU AI Act risk class</Label>
                <select
                  value={s.riskClass}
                  onChange={(e) => update(i, { riskClass: e.target.value as AiSystem['riskClass'] })}
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="minimal">Minimal</option>
                  <option value="limited">Limited</option>
                  <option value="high">High</option>
                  <option value="unacceptable">Unacceptable</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <Label>Intended use</Label>
                <Textarea
                  rows={2}
                  value={s.useCase}
                  onChange={(e) => update(i, { useCase: e.target.value })}
                  placeholder="e.g. drafts safety narratives from raw ICSR data; never closes cases"
                />
              </div>
            </div>
            {systems.length > 1 && (
              <div className="mt-2 flex justify-end">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setSystems((prev) => prev.filter((_, idx) => idx !== i))}
                >
                  <Trash2 className="mr-1 h-3 w-3" /> Remove
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => setSystems((prev) => [...prev, { name: '', vendor: '', model: '', useCase: '', riskClass: 'limited' }])}
        >
          <Plus className="mr-1 h-3 w-3" /> Add another system
        </Button>
        <Button type="submit" disabled={!valid || saving}>
          {saving ? 'Saving...' : 'Save and continue'}
        </Button>
      </div>
    </form>
  );
}

// --- Step 3 -------------------------------------------------------------------

interface RiskItem {
  area: string;
  likelihood: number; // 1..5
  impact: number; // 1..5
  mitigation: string;
  residualRisk: string;
}

function Step3Risks({
  initial,
  onSave,
  saving,
}: {
  initial: Record<string, unknown>;
  onSave: (data: Record<string, unknown>) => void;
  saving: boolean;
}) {
  const [risks, setRisks] = useState<RiskItem[]>(
    Array.isArray(initial.risks) && (initial.risks as RiskItem[]).length > 0
      ? (initial.risks as RiskItem[])
      : [{ area: '', likelihood: 3, impact: 3, mitigation: '', residualRisk: '' }],
  );
  const update = (i: number, patch: Partial<RiskItem>) =>
    setRisks((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const valid = risks.length > 0 && risks.every((r) => r.area.trim().length > 1);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid) return;
        onSave({ risks });
      }}
      className="space-y-4"
    >
      <div className="space-y-3">
        {risks.map((r, i) => (
          <div key={i} className="rounded-md border p-3">
            <div className="grid gap-3 md:grid-cols-2">
              <div className="md:col-span-2">
                <Label>Risk area</Label>
                <Input value={r.area} onChange={(e) => update(i, { area: e.target.value })} placeholder="e.g. AI hallucination on causality assessment" required />
              </div>
              <div>
                <Label>Likelihood (1-5)</Label>
                <Input type="number" min={1} max={5} value={r.likelihood} onChange={(e) => update(i, { likelihood: Number(e.target.value) })} />
              </div>
              <div>
                <Label>Impact (1-5)</Label>
                <Input type="number" min={1} max={5} value={r.impact} onChange={(e) => update(i, { impact: Number(e.target.value) })} />
              </div>
              <div className="md:col-span-2">
                <Label>Mitigation</Label>
                <Textarea rows={2} value={r.mitigation} onChange={(e) => update(i, { mitigation: e.target.value })} placeholder="e.g. mandatory PV physician review before any case closure" />
              </div>
              <div className="md:col-span-2">
                <Label>Residual risk</Label>
                <Input value={r.residualRisk} onChange={(e) => update(i, { residualRisk: e.target.value })} placeholder="e.g. Low" />
              </div>
            </div>
            {risks.length > 1 && (
              <div className="mt-2 flex justify-end">
                <Button type="button" size="sm" variant="outline" onClick={() => setRisks((prev) => prev.filter((_, idx) => idx !== i))}>
                  <Trash2 className="mr-1 h-3 w-3" /> Remove
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => setRisks((prev) => [...prev, { area: '', likelihood: 3, impact: 3, mitigation: '', residualRisk: '' }])}
        >
          <Plus className="mr-1 h-3 w-3" /> Add risk
        </Button>
        <Button type="submit" disabled={!valid || saving}>
          {saving ? 'Saving...' : 'Save and continue'}
        </Button>
      </div>
    </form>
  );
}

// --- Step 4 -------------------------------------------------------------------

interface RopaActivity {
  name: string;
  lawfulBasis: string;
  dataCategories: string;
  retention: string;
}

function Step4Ropa({
  initial,
  onSave,
  saving,
}: {
  initial: Record<string, unknown>;
  onSave: (data: Record<string, unknown>) => void;
  saving: boolean;
}) {
  const [activities, setActivities] = useState<RopaActivity[]>(
    Array.isArray(initial.activities) && (initial.activities as RopaActivity[]).length > 0
      ? (initial.activities as RopaActivity[])
      : [{ name: '', lawfulBasis: 'legitimate-interests', dataCategories: '', retention: '' }],
  );
  const update = (i: number, patch: Partial<RopaActivity>) =>
    setActivities((prev) => prev.map((a, idx) => (idx === i ? { ...a, ...patch } : a)));
  const valid = activities.length > 0 && activities.every((a) => a.name.trim().length > 1);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid) return;
        onSave({ activities });
      }}
      className="space-y-4"
    >
      <div className="space-y-3">
        {activities.map((a, i) => (
          <div key={i} className="rounded-md border p-3">
            <div className="grid gap-3 md:grid-cols-2">
              <div className="md:col-span-2">
                <Label>Activity</Label>
                <Input value={a.name} onChange={(e) => update(i, { name: e.target.value })} placeholder="e.g. Receipt and processing of ICSRs from healthcare professionals" required />
              </div>
              <div>
                <Label>Lawful basis (Article 6)</Label>
                <select
                  value={a.lawfulBasis}
                  onChange={(e) => update(i, { lawfulBasis: e.target.value })}
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="consent">Consent</option>
                  <option value="contract">Contract</option>
                  <option value="legal-obligation">Legal obligation</option>
                  <option value="vital-interests">Vital interests</option>
                  <option value="public-task">Public task</option>
                  <option value="legitimate-interests">Legitimate interests</option>
                </select>
              </div>
              <div>
                <Label>Retention period</Label>
                <Input value={a.retention} onChange={(e) => update(i, { retention: e.target.value })} placeholder="e.g. 30 years (post-marketing)" />
              </div>
              <div className="md:col-span-2">
                <Label>Data categories</Label>
                <Textarea rows={2} value={a.dataCategories} onChange={(e) => update(i, { dataCategories: e.target.value })} placeholder="Patient initials, AE narrative, healthcare professional contact" />
              </div>
            </div>
            {activities.length > 1 && (
              <div className="mt-2 flex justify-end">
                <Button type="button" size="sm" variant="outline" onClick={() => setActivities((prev) => prev.filter((_, idx) => idx !== i))}>
                  <Trash2 className="mr-1 h-3 w-3" /> Remove
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => setActivities((prev) => [...prev, { name: '', lawfulBasis: 'legitimate-interests', dataCategories: '', retention: '' }])}
        >
          <Plus className="mr-1 h-3 w-3" /> Add activity
        </Button>
        <Button type="submit" disabled={!valid || saving}>
          {saving ? 'Saving...' : 'Save and continue'}
        </Button>
      </div>
    </form>
  );
}

// --- Step 5 -------------------------------------------------------------------
//
// Inline upload widget. The user drops a PDF / DOCX directly here (no
// new-tab detour). On successful upload the step auto-completes and the
// document id is saved to OnboardingState.data.step5 so we can show what
// was uploaded if the user revisits.

interface Step5Payload extends Record<string, unknown> {
  documentId?: string;
  fileName?: string;
  fileSizeBytes?: number;
  uploadedAt?: string;
  acknowledged?: boolean;
}

function Step5Doc({
  done,
  onComplete,
  saving,
}: {
  done: boolean;
  onComplete: (payload: Step5Payload) => void;
  saving: boolean;
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadedDoc, setUploadedDoc] = useState<{ id: string; fileName: string; fileSizeBytes: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleFile(file: File) {
    setError(null);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post<{ id: string; fileName: string; fileSizeBytes: number }>(
        '/api/documents',
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      const doc = res.data;
      setUploadedDoc({ id: doc.id, fileName: doc.fileName, fileSizeBytes: doc.fileSizeBytes });
      onComplete({
        documentId: doc.id,
        fileName: doc.fileName,
        fileSizeBytes: doc.fileSizeBytes,
        uploadedAt: new Date().toISOString(),
        acknowledged: true,
      });
      toast.success('Document uploaded', `${doc.fileName} -- step 5 complete.`);
    } catch (err) {
      setError(extractApiError(err).message);
    } finally {
      setUploading(false);
    }
  }

  function onDrop(e: React.DragEvent<HTMLLabelElement>) {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f) void handleFile(f);
  }

  return (
    <div className="space-y-4 text-sm">
      <p>
        Upload one regulatory document so you can see Praxara classify and extract it. CSRs, EPARs and PSURs all
        work; PDF or DOCX up to 50 MB. Anything sensitive can be deleted afterwards from the Documents page.
      </p>

      {/* Drop zone */}
      <label
        htmlFor="onboarding-step5-file"
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        className={cn(
          'flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-8 text-center text-sm transition-colors',
          uploading
            ? 'cursor-not-allowed border-primary/40 bg-primary/5'
            : 'cursor-pointer border-border hover:border-primary/40 hover:bg-muted/40',
        )}
      >
        {uploading ? (
          <>
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <span className="text-muted-foreground">Uploading...</span>
          </>
        ) : (
          <>
            <Upload className="h-6 w-6 text-muted-foreground" />
            <span>
              <strong>Drop a file here</strong> or click to browse
            </span>
            <span className="text-xs text-muted-foreground">PDF / DOCX -- one file is enough to complete the step</span>
          </>
        )}
        <input
          id="onboarding-step5-file"
          type="file"
          accept=".pdf,.docx,.doc"
          className="sr-only"
          disabled={uploading}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void handleFile(f);
          }}
        />
      </label>

      {error && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
          <X className="mt-0.5 h-3 w-3 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {uploadedDoc && (
        <div className="flex items-start gap-3 rounded-md border border-emerald-300/60 bg-emerald-50/40 px-3 py-2 text-xs dark:border-emerald-900/40 dark:bg-emerald-950/30">
          <FileText className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" />
          <div className="min-w-0 flex-1">
            <div className="font-medium text-emerald-900 dark:text-emerald-300">{uploadedDoc.fileName}</div>
            <div className="text-[10px] text-emerald-800 dark:text-emerald-400">
              {(uploadedDoc.fileSizeBytes / 1024 / 1024).toFixed(1)} MB -- extraction is queued.
            </div>
          </div>
          <a
            href={`/documents/${uploadedDoc.id}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 hover:underline"
          >
            Open <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      )}

      {/* Skip path for users that already uploaded earlier (e.g. via Documents page). */}
      {!uploadedDoc && done && (
        <p className="text-xs text-muted-foreground">
          You marked this step done previously. Continue with <strong>First workflow</strong>.
        </p>
      )}
      {!uploadedDoc && !done && (
        <button
          type="button"
          onClick={() => onComplete({ acknowledged: true })}
          disabled={saving}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          Already uploaded one elsewhere? Skip this step &rarr;
        </button>
      )}
    </div>
  );
}

// --- Step 6 -------------------------------------------------------------------

const WORKFLOW_OPTIONS: { id: string; label: string; description: string }[] = [
  { id: 'csr-extraction', label: 'CSR extraction', description: 'Clinical Study Report intelligence + reviewer canvas + e-sign.' },
  { id: 'psur-drafting', label: 'PSUR drafting', description: 'Periodic Safety Update Report drafting from prior-period data.' },
  { id: 'rmp-authoring', label: 'RMP authoring', description: 'Risk Management Plan section drafting.' },
  { id: 'signal-validation', label: 'Signal validation', description: 'Signal management triage with disproportionality + GVP IX trail.' },
  { id: 'pv-icsr', label: 'PV ICSR processing', description: 'ICSR ingest, MedDRA suggestion, narrative draft, E2B(R3) emit.' },
];

function Step6Workflow({
  initial,
  onSave,
  saving,
}: {
  initial: Record<string, unknown>;
  onSave: (data: Record<string, unknown>) => void;
  saving: boolean;
}) {
  const [pick, setPick] = useState<string>((initial.workflowId as string | undefined) ?? '');
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!pick) return;
        onSave({ workflowId: pick });
      }}
      className="space-y-4"
    >
      <div className="grid gap-2">
        {WORKFLOW_OPTIONS.map((w) => (
          <label
            key={w.id}
            className={cn(
              'flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors',
              pick === w.id ? 'border-primary bg-primary/5' : 'hover:bg-muted',
            )}
          >
            <input
              type="radio"
              name="workflow"
              value={w.id}
              checked={pick === w.id}
              onChange={() => setPick(w.id)}
              className="mt-1 h-4 w-4 accent-primary"
            />
            <div>
              <div className="text-sm font-medium">{w.label}</div>
              <div className="text-xs text-muted-foreground">{w.description}</div>
            </div>
          </label>
        ))}
      </div>
      <Button type="submit" disabled={!pick || saving}>
        {saving ? 'Saving...' : 'Save and continue'}
      </Button>
    </form>
  );
}

// --- Step 7 -------------------------------------------------------------------

function Step7Pack({
  onComplete,
  completing,
  ready,
}: {
  onComplete: () => void;
  completing: boolean;
  ready: boolean;
}) {
  return (
    <div className="space-y-4 text-sm">
      <p>
        Generating your first Compliance Pack confirms onboarding and locks the answers from steps 1-4 into your
        baseline artefacts (AI inventory, risk register, ROPA, model cards).
      </p>
      <p className="text-muted-foreground">
        You can re-export at any time from <code>/compliance-pack</code>; existing packs are not overwritten.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Button asChild variant="outline">
          <a href="/compliance-pack" target="_blank" rel="noreferrer">
            Open Compliance Pack <ExternalLink className="ml-1 h-3 w-3" />
          </a>
        </Button>
        <Button onClick={onComplete} disabled={!ready || completing}>
          {completing ? 'Finalising...' : 'Complete onboarding'}
        </Button>
      </div>
      {!ready && (
        <p className="text-xs text-amber-700 dark:text-amber-400">Finish steps 1-6 first.</p>
      )}
    </div>
  );
}
